import { ChevronDown } from "lucide-react";

/**
 * Disclosure — accessible accordion/disclosure with a smooth height transition.
 * WAI-ARIA: heading (h3) wraps the toggle button; aria-expanded + aria-controls
 * + a labelled region. Height animates via CSS grid-template-rows (0fr→1fr),
 * so content remains in the DOM and reduced-motion is respected in CSS.
 *
 * Props:
 *   id       – unique id (also used for the panel id / aria-controls)
 *   isOpen   – controlled open state
 *   onToggle – () => void
 *   trigger  – the visible trigger content (number + title)
 *   children – panel content (revealed on open)
 */
export function Disclosure({ id, isOpen, onToggle, trigger, children }) {
  return (
    <div className="border-b border-memo-line">
      <h3>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={`${id}-panel`}
          onClick={onToggle}
          className="w-full flex items-center justify-between gap-6 py-5 text-left group"
        >
          {trigger}
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={`text-memo-muted shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"}`}
          />
        </button>
      </h3>
      <div
        id={`${id}-panel`}
        role="region"
        aria-hidden={!isOpen}
        className={`disclosure ${isOpen ? "open" : ""}`}
      >
        <div className="disclosure-inner">
          <div className="pb-7">{children}</div>
        </div>
      </div>
    </div>
  );
}
